import React from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  PictureAsPdf,
  TableChart,
  Print,
  Download,
  ArrowDropDown,
  Email,
} from '@mui/icons-material';

interface ExportToolbarProps {
  onExportPDF?: () => void;
  onExportExcel?: () => void;
  onPrint?: () => void;
  onEmail?: () => void;
  isExporting?: boolean;
  disabled?: boolean;
  variant?: 'buttons' | 'dropdown';
  size?: 'small' | 'medium' | 'large';
  showPrint?: boolean;
  showEmail?: boolean;
}

const ExportToolbar: React.FC<ExportToolbarProps> = ({
  onExportPDF,
  onExportExcel,
  onPrint,
  onEmail,
  isExporting = false,
  disabled = false,
  variant = 'buttons',
  size = 'small',
  showPrint = true,
  showEmail = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExport = (action: () => void) => {
    handleClose();
    action();
  };

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
    } else {
      window.print();
    }
  };

  if (variant === 'dropdown') {
    return (
      <Box>
        <Button
          variant="contained"
          size={size}
          startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <Download />}
          endIcon={<ArrowDropDown />}
          onClick={handleClick}
          disabled={disabled || isExporting}
        >
          Export
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {onExportPDF && (
            <MenuItem onClick={() => handleExport(onExportPDF)}>
              <ListItemIcon>
                <PictureAsPdf fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Export as PDF</ListItemText>
            </MenuItem>
          )}
          {onExportExcel && (
            <MenuItem onClick={() => handleExport(onExportExcel)}>
              <ListItemIcon>
                <TableChart fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>Export as Excel</ListItemText>
            </MenuItem>
          )}
          {(onExportPDF || onExportExcel) && (showPrint || showEmail) && <Divider />}
          {showPrint && (
            <MenuItem onClick={() => handleExport(handlePrint)}>
              <ListItemIcon>
                <Print fontSize="small" />
              </ListItemIcon>
              <ListItemText>Print</ListItemText>
            </MenuItem>
          )}
          {showEmail && onEmail && (
            <MenuItem onClick={() => handleExport(onEmail)}>
              <ListItemIcon>
                <Email fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>Send via Email</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <ButtonGroup variant="outlined" size={size} disabled={disabled || isExporting}>
        {onExportPDF && (
          <Button
            onClick={onExportPDF}
            startIcon={isExporting ? <CircularProgress size={16} /> : <PictureAsPdf />}
            color="error"
          >
            PDF
          </Button>
        )}
        {onExportExcel && (
          <Button
            onClick={onExportExcel}
            startIcon={isExporting ? <CircularProgress size={16} /> : <TableChart />}
            color="success"
          >
            Excel
          </Button>
        )}
        {showPrint && (
          <Button
            onClick={handlePrint}
            startIcon={<Print />}
          >
            Print
          </Button>
        )}
      </ButtonGroup>
      {showEmail && onEmail && (
        <Button
          variant="outlined"
          size={size}
          onClick={onEmail}
          startIcon={<Email />}
          disabled={disabled}
        >
          Email
        </Button>
      )}
    </Box>
  );
};

export default ExportToolbar;
